import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    TextField, 
    SelectChangeEvent, 
    useMediaQuery,
    Dialog,
    InputAdornment,
    DialogContent,
    DialogActions,
    Menu,        
    MenuItem,
    Popover,
    Select,
    CssBaseline, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import DatePicker from '../../Components/DatePicker/date-picker.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import QueryWidget from '../../Components/QueryWidget/query-widget.tsx';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import '../../Components/Button/button-style.scss';
import { getTripsOrdersReport, getVehicleUsageReport, getClients, getWarehouses, getSpeedReport, getStoppageReport } from './action.ts'; 

const DATE_FILTER_OPTIONS = [
    { id: 'Today', label: 'Today' },
    { id: 'Yesterday', label: 'Yesterday' },
    { id: 'This month', label: 'This month' },
    { id: 'Last month', label: 'Last month' },
    { id: 'Custom', label: 'Custom' }
];

const SPEED_TYPE_OPTIONS = [
    { id: 'Average', label: 'Average' },
    { id: 'Highest', label: 'Highest' }
];

const Reports: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
      // STATE DECLARATIONS
   
    const [dateFilter, setDateFilter] = useState<string>('Today');
    const [speedType, setSpeedType] = useState<string>('Average');
    const [vehicleSearch, setVehicleSearch] = useState<string>('');
    const [tempVehicleSearch, setTempVehicleSearch] = useState<string>('');
    const [clientOptions, setClientOptions] = useState<{id: string, label: string}[]>([{ id: 'All', label: 'All Clients' }]);
    const [selectedClient, setSelectedClient] = useState<{id: string, label: string}>({ id: 'All', label: 'All Clients' });
    const [selectedWarehouse, setSelectedWarehouse] = useState<{id: string, label: string}>({ id: 'All', label: 'All Warehouses' });
    const [warehouseOptions, setWarehouseOptions] = useState<{id: string, label: string}[]>([{ id: 'All', label: 'All Warehouses' }]);
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const isFilterOpen = Boolean(filterAnchorEl);
    const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
    const [customFromDate, setCustomFromDate] = useState<string>('');
    const [customToDate, setCustomToDate] = useState<string>('');
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
    const isDownloadMenuOpen = Boolean(downloadMenuAnchor);
    const [allWarehouses, setAllWarehouses] = useState<any[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<string>('0');
    const [pageSize, setPageSize] = useState<number>(10); // Defaulting to 20
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
    const userCompaniesId = parseInt(userDetailsFromStorage?.companies_id || '0', 10);
    const isClientAdmin = userDetailsFromStorage?.role_name === 'client_company_admin';
     const roleFeatures = JSON.parse(localStorage.getItem('roleFeatures') || '[]');
    const hasFeature = (featureName: string) => {
        if (!roleFeatures) return false;
        const target = featureName.toLowerCase();
        const containsFeature = (arr: any[]) => arr.some((f: any) => 
            (typeof f === 'string' && f.toLowerCase() === target) ||
            (f?.name && f.name.toLowerCase() === target) ||
            (f?.feature && f.feature.toLowerCase() === target)
        );

        if (Array.isArray(roleFeatures)) {
            const configObj = roleFeatures.find(item => item && typeof item === 'object' && item.enabled);
            if (configObj) {
                if (configObj.enabled && Array.isArray(configObj.enabled) && containsFeature(configObj.enabled)) return true;
            }
            return containsFeature(roleFeatures);
        }

        if (typeof roleFeatures === 'object') {
            if (roleFeatures.enabled && Array.isArray(roleFeatures.enabled) && containsFeature(roleFeatures.enabled)) return true;
            return !!roleFeatures[featureName] || !!roleFeatures[target];
        }

        return false;
    };

    // DYNAMIC REPORT OPTIONS BASED ON FEATURES
   
    const hasClientFeature = hasFeature('client') || hasFeature('clients');
    const hasWarehouseFeature = hasFeature('warehouse') || hasFeature('warehouses');
     const reportOptions = [];
    if (hasFeature('report_vehicle_usage')) {
        reportOptions.push({ id: 'Vehicle Usage', label: 'Vehicle Usage' });
    }
    if (hasFeature('report_trips')) {
        reportOptions.push({ id: 'Trips', label: 'Trips Report' });
    }
    if (hasFeature('report_vehicle_speed')) {
        reportOptions.push({ id: 'Speed Report', label: 'Speed Report' });
    }
    if (hasFeature('report_vehicle_stops')) {
        reportOptions.push({ id: 'Stoppage Report', label: 'Stoppage Report' });
    }
    if (hasFeature('report_orders')) {
        reportOptions.push({ id: 'Orders', label: 'Orders Report' });
    }

    // Fallback if no report features are enabled
    if (reportOptions.length === 0) {
        reportOptions.push({ id: 'Vehicle Usage', label: 'Vehicle Usage' });
    }

   const [selectedReport, setSelectedReport] = useState<string>(reportOptions[0].id);
    const handleClearFilters = () => {
        setTempVehicleSearch('');
        setVehicleSearch('');
        setSelectedClient({ id: 'All', label: 'All Clients' });
        setSelectedWarehouse({ id: 'All', label: 'All Warehouses' });
        setDateFilter('Today'); 
        setPage('0');
    };

    useEffect(() => {
        if (hasClientFeature) {
            getClients(
                (response: any) => {
                    const data = response?.message?.clients_vendors || [];
                    const formattedClients = data.map((item: any) => ({
                        id: item.id.toString(),
                        label: item.name
                    }));
                    setClientOptions([{ id: 'All', label: 'All Clients' }, ...formattedClients]);
                },
                (error: any) => {
                    console.error("Failed to fetch clients", error);
                }
            );
        }

        if (hasWarehouseFeature) {
            getWarehouses(
                (response: any) => {
                    const data = response?.message?.warehouses || [];
                    setAllWarehouses(data); 
                },
                (error: any) => {
                    console.error("Failed to fetch warehouses", error);
                }
            );
        }
    }, [hasClientFeature, hasWarehouseFeature]);

    useEffect(() => {
        if (hasWarehouseFeature) {
            const isSpecificClient = selectedClient && selectedClient.id !== 'All';
            
            const filteredWarehouses = isSpecificClient
                ? allWarehouses.filter((warehouse: any) =>
                    warehouse.client_name?.toLowerCase() === selectedClient.label.toLowerCase()
                )
                : allWarehouses;

            const options = filteredWarehouses.map((warehouse: any) => ({
                id: warehouse.id.toString(),
                label: warehouse.name,
            }));

            if (isSpecificClient && options.length === 0) {
                setWarehouseOptions([{ id: 'none', label: 'No Warehouses' }]);
                setSelectedWarehouse({ id: 'none', label: 'No Warehouses' });
            } else {
                setWarehouseOptions([{ id: 'All', label: 'All Warehouses' }, ...options]);
            }
        }
    }, [selectedClient, allWarehouses, hasWarehouseFeature]);

    useEffect(() => {
        let baseColumns: GridColDef[] = [
            { field: 'sl_no', headerName: 'Sl. No.', width: 80, headerAlign: 'center', align: 'center' },
            { field: 'date', headerName: 'Date', flex: 1, minWidth: 110, headerAlign: 'center', align: 'center' },
            { field: 'vehicle_no', headerName: 'Vehicle No:', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
            { field: 'driver', headerName: 'Driver', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' }, 
            { 
                field: 'warehouse_name',
                headerName: 'Warehouse', 
                flex: 1, 
                minWidth: 130, 
                headerAlign: 'center', 
                align: 'center'
            },
            { field: 'start_time', headerName: 'Start Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'end_time', headerName: 'End Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'kms', headerName: 'Kms', width: 90, headerAlign: 'center', align: 'center' },
            { field: 'stops', headerName: 'Stops', width: 90, headerAlign: 'center', align: 'center' },
            { field: 'running_duration', headerName: 'Running Duration', flex: 1, minWidth: 140, headerAlign: 'center', align: 'center' },
        ];

        if (userCompaniesId === 10 || userCompaniesId === 85) {
            baseColumns = baseColumns.filter(
                (col) => col.field !== 'warehouse_name' && col.field !== 'driver'
            );
        }

        let usageAndTripsColumns: GridColDef[] = [...baseColumns];

        if (userCompaniesId !== 10 && userCompaniesId !== 85) {
             usageAndTripsColumns.push({ 
                 field: 'engine_stops', 
                 headerName: 'Engine Stops', 
                 flex: 1, 
                 headerAlign: 'center', 
                 align: 'center' 
             });
        }

        const ordersColumns: GridColDef[] = [
            ...baseColumns,
            { field: 'orders_picked', headerName: 'Orders Picked', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'orders_delivered', headerName: 'Orders Delivered', flex: 1, headerAlign: 'center', align: 'center' }
        ];

       const stoppageColumns: GridColDef[] = [
            { field: 'sl_no', headerName: 'Sl. No.', width: 80, headerAlign: 'center', align: 'center' },
            { field: 'vehicle_no', headerName: 'Vehicle No:', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
            { field: 'status', headerName: 'Status', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'start_time', headerName: 'Start Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'end_time', headerName: 'End Time', flex: 1, headerAlign: 'center', align: 'center' },
            { field: 'running_duration', headerName: 'Duration', flex: 1, headerAlign: 'center', align: 'center' },
            { 
                field: 'address', 
                headerName: 'Address', 
                flex: 1, 
                headerAlign: 'center', 
                align: 'center',
                renderCell: (params) => {
                    if (params.row.geofence_name) {
                        return <span>{params.row.geofence_name}</span>;
                    } 
                    else if (params.row.latitude && params.row.longitude) {
                        return (
                            <IconButton 
                                color="primary" 
                                onClick={() => window.open(`https://www.google.com/maps?q=${params.row.latitude},${params.row.longitude}`, '_blank')}
                                title="View on Google Maps"
                            >
                                <LocationOnIcon />
                            </IconButton>
                        );
                    }
                    return '-';
                }
            }
        ];

        const speedColumns: GridColDef[] = [
            { field: 'sl_no', headerName: 'Sl. No.', width: 80, headerAlign: 'center', align: 'center' },
            { field: 'date', headerName: 'Date', flex: 1, minWidth: 110, headerAlign: 'center', align: 'center' },
            { field: 'vehicle_no', headerName: 'Vehicle No:', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
            { field: 'speed', headerName: speedType === 'Average' ? 'Average Speed' : 'Highest Speed', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' }
        ];

        if (selectedReport === 'Orders') {
            setColumns(ordersColumns);
        } else if (selectedReport === 'Stoppage Report') {
            setColumns(stoppageColumns);
        } else if (selectedReport === 'Speed Report') {
            setColumns(speedColumns);
        } else {
            setColumns(usageAndTripsColumns);
        }

        if (!(dateFilter === 'Custom' && isDateModalOpen)) {
            fetchReportData(); 
        }
    }, [selectedReport, page, pageSize, dateFilter, selectedClient, selectedWarehouse, vehicleSearch, speedType]);

    const formatDuration = (totalSeconds: number | null) => {
        if (!totalSeconds) return "0 hr, 0 mins";
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        if (hours === 0) return `${minutes} mins`;
        return `${hours} hr, ${minutes} mins`;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    };

    const getDateRange = (filter: string) => {
        if (filter === 'Custom') {
            return { from_date: customFromDate, to_date: customToDate };
        }

        const today = new Date();
        let fromDate = new Date();
        let toDate = new Date();

        if (filter === 'Yesterday') {
            fromDate.setDate(today.getDate() - 1);
            toDate.setDate(today.getDate() - 1);
        } else if (filter === 'This month') {
            fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        } else if (filter === 'Last month') {
            fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        }

        const format = (d: Date) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            from_date: format(fromDate),
            to_date: format(toDate)
        };
    };

    const formatTimeAMPM = (timeString: string) => {
        if (!timeString) return '-';
        
        const safeDateString = timeString.replace(' ', 'T');
        const date = new Date(safeDateString);
        
        if (isNaN(date.getTime())) return timeString;
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const fetchReportData = (apiFormat: string = '') => {
        if (dateFilter === 'Custom' && (!customFromDate || !customToDate)) return;

        setLoading(true);
        const { from_date, to_date } = getDateRange(dateFilter);

        const handleSuccess = (response: any) => {
            const rawData = response?.reports || response?.data || [];
            
            const mappedData = Array.isArray(rawData) ? rawData.map((item: any, index: number) => {            
                const rawStartTime = item.start_time_ist || item.first_moving_start_ist || item.start_time_formatted || item.start_time;
                const rawEndTime = item.end_time_ist || item.last_moving_end_ist || item.end_time_formatted || item.end_time;
                const speedData = item.speed || item.max_speed || item.avg_speed || '';

                return {
                    ...item,
                    id: item.vehicles_id || index + 1, 
                    sl_no: (parseInt(page) * pageSize) + index + 1,
                    date: item.date || from_date,
                    vehicle_no: item.vehicle_number || item.vehicle_no || '',
                    driver: item.driver_name || item.driver || '',
                    start_time: formatTimeAMPM(rawStartTime),
                    end_time: formatTimeAMPM(rawEndTime),
                    
                    kms: item.total_km || item.kms || '',
                    stops: item.total_stops || item.stops || '',
                    running_duration: item.duration_sec ? formatDuration(item.duration_sec) : (item.moving_duration_sec ? formatDuration(item.moving_duration_sec) : formatDuration(item.running_duration_sec)),
                    engine_stops: item?.engine_stop_count || item?.engine_stops || '',
                    status: item.status || '',
                    location: item.geofence_name || 'Unknown',
                    latitude: item.latitude || '',
                    longitude: item.longitude || '',
                    speed: speedData,
                };
            }) : [];

            setRows(mappedData);
            const totalCount = response?.pagination?.total_records || response?.total_records || mappedData.length;
            setTotalRecords(totalCount);
            setLoading(false);
        };

        const handleError = (errorMsg: any) => {
            toast.error(typeof errorMsg === 'string' ? errorMsg : `Failed to fetch ${selectedReport} report`);
            setRows([]);
            setTotalRecords(0);
            setLoading(false);
        };

        const apiWarehouse = selectedWarehouse.label === 'All Warehouses' ? '' : selectedWarehouse.label;
        const apiClient = selectedClient.label === 'All Clients' ? '' : selectedClient.label;
        
        if (selectedReport === 'Vehicle Usage') {
            getVehicleUsageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleSuccess, handleError, apiWarehouse, apiClient);
        } else if (selectedReport === 'Stoppage Report') {
            getStoppageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleSuccess, handleError);
        } else if (selectedReport === 'Speed Report') {
            const mode = speedType === 'Highest' ? 'highest' : 'average';
            getSpeedReport(from_date, to_date, vehicleSearch, page, pageSize,mode, apiFormat, handleSuccess, handleError );
        } else {
            getTripsOrdersReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleSuccess, handleError, apiWarehouse, apiClient);
        }
    };

    const handleReportChange = (event: SelectChangeEvent<string>) => {
        setSelectedReport(event.target.value);
        setPage('0'); 
        setDateFilter('Today');
        setSpeedType('Average'); 
        setCustomFromDate('');
        setCustomToDate('');
        setSelectedClient({ id: 'All', label: 'All Clients' });
        setSelectedWarehouse({ id: 'All', label: 'All Warehouses' });
        setTempVehicleSearch('');
        setVehicleSearch('');
    };

    const handleDateChange = (event: SelectChangeEvent<string>) => {
        const selectedValue = event.target.value;
        setDateFilter(selectedValue);
        
        if (selectedValue === 'Custom') {
            setIsDateModalOpen(true);
        } else {
            setPage('0');
            setVehicleSearch(tempVehicleSearch);
        }
    };
    const showEntries = [
        {id:'10',  label:'10'},
        { id: '20', label: '20' },
        { id: '50', label: '50' },
        { id: '100', label: '100' },
    ];
    const handleApplyCustomDates = () => {
        if (!customFromDate || !customToDate) {
            toast.warning("Please select both From and To dates.");
            return;
        }

        const from = new Date(customFromDate);
        const to = new Date(customToDate);

        if (to < from) {
            toast.warning("'To' date cannot be before 'From' date.");
            return;
        }

        const diffTime = Math.abs(to.getTime() - from.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 

        if (diffDays > 30) {
            toast.warning("Date range cannot exceed 30 days.");
            return;
        }

        setIsDateModalOpen(false);
        setVehicleSearch(tempVehicleSearch);
        setPage('0');
        fetchReportData(); 
    };

    const handleCloseModal = () => {
        setIsDateModalOpen(false);
        if (!customFromDate || !customToDate) {
            setDateFilter('Today');
        }
    };

    const getMaxToDate = () => {
        if (!customFromDate) return undefined;
        const fromDateObj = new Date(customFromDate);
        fromDateObj.setDate(fromDateObj.getDate() + 30);
        return fromDateObj.toISOString().split('T')[0];
    };

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => { 
        setPage((newPage - 1).toString()); 
    };

    // Handler for changing entries per page
    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        setPageSize(Number(event.target.value));
        setPage('0'); // Reset to the first page when the page size changes
    };

    const handleOpenDownloadMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setDownloadMenuAnchor(null);
    };

    const handleApplyFilter = () => {
        setVehicleSearch(tempVehicleSearch);
        setPage('0'); 
    };
    const updateField = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (!isNaN(Number(value))) {
            setPageSize(Number(value));
            setPage(0);
        }
    };
    const handleDownloadFormat = (format: 'Excel' | 'CSV' | 'PDF') => {
        handleCloseDownloadMenu();

        if (totalRecords === 0) {
            toast.warning("No data available to download.");
            return;
        }

        let apiFormat = '';
        let mimeType = '';
        let fileExtension = '';

        if (format === 'Excel') {
            apiFormat = 'xlsx';
            mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            fileExtension = 'xlsx';
        } else if (format === 'CSV') {
            apiFormat = 'csv';
            mimeType = 'text/csv';
            fileExtension = 'csv';
        } else if (format === 'PDF') {
            apiFormat = 'pdf';
            mimeType = 'application/pdf';
            fileExtension = 'pdf';
        }

        const { from_date, to_date } = getDateRange(dateFilter);
        const apiWarehouse = selectedWarehouse.label === 'All Warehouses' ? '' : selectedWarehouse.label;
        const apiClient = selectedClient.label === 'All Clients' ? '' : selectedClient.label;

        const toastId = toast.loading(`Downloading ${format} file... Please wait.`);

        const handleDownloadSuccess = (response: any) => {
            try {
                const binaryData = response.data || response; 

                const blob = new Blob([binaryData], { type: mimeType });
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                
                const fileName = `${selectedReport.replace(/\s+/g, '_')}_Report.${fileExtension}`;
                link.setAttribute('download', fileName);
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.update(toastId, { render: `${format} downloaded successfully!`, type: "success", isLoading: false, autoClose: 3000 });
            } catch (error) {
                console.error("Blob conversion error:", error);
                toast.update(toastId, { render: "Error processing the downloaded file.", type: "error", isLoading: false, autoClose: 3000 });
            }
        };

        const handleDownloadError = (errorMsg: any) => {
            toast.update(toastId, { render: typeof errorMsg === 'string' ? errorMsg : `Failed to download ${format}`, type: "error", isLoading: false, autoClose: 3000 });
        };

        if (selectedReport === 'Vehicle Usage') {
            getVehicleUsageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError, apiWarehouse, apiClient);
        } else if (selectedReport === 'Stoppage Report') {
            getStoppageReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError);
        } else if (selectedReport === 'Speed Report') {
            const mode = speedType === 'Highest' ? 'highest' : 'average';
            getSpeedReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError, mode);
        } else {
            getTripsOrdersReport(from_date, to_date, vehicleSearch, page, pageSize, apiFormat, handleDownloadSuccess, handleDownloadError, apiWarehouse, apiClient);
        }
    };

    const renderReportAndDateFilters = (isMobileColumn: boolean) => (
        <Box 
            display="flex" 
            gap={2} 
            sx={{ 
                flexDirection: isMobileColumn ? { xs: 'column', md: 'row' } : 'row', 
                alignItems: isMobileColumn ? { xs: 'stretch', md: 'center' } : 'center',
                flexWrap: 'nowrap',
                width: isMobileColumn ? { xs: '100%', md: 'auto' } : 'auto'
            }}
        >
            <Box sx={{ width: isMobileColumn ? { xs: '100%', md: '150px' } : '150px', minWidth: isMobileColumn ? 'auto' : '150px' }}>
                <Dropdown
                    options={reportOptions}
                    selectedValue={selectedReport}
                    handleChange={handleReportChange}
                    width="150px"
                    height="40px" 
                />
            </Box>

            {selectedReport === 'Speed Report' && (
                <Box sx={{ width: isMobileColumn ? { xs: '120px', md: '120px' } : '120px', minWidth: isMobileColumn ? 'auto' : '120px' }}>
                    <Dropdown
                        options={SPEED_TYPE_OPTIONS}
                        selectedValue={speedType}
                        handleChange={(e: SelectChangeEvent<string>) => {
                            setSpeedType(e.target.value);
                            setPage('0'); 
                        }}
                        width="120px"
                        height="40px"
                    />
                </Box>
            )}

            <Box 
                display="flex" 
                alignItems="center" 
                gap={0.5} 
                sx={{ width: isMobileColumn ? { xs: '100%', md: '130px' } : 'auto', minWidth: isMobileColumn ? 'auto' : '130px' }}
            >
                <Select
                    value={dateFilter}
                    onChange={handleDateChange}
                    sx={{ width: '150px', height: '40px' }}
                >
                    {DATE_FILTER_OPTIONS.map((option) => (
                        <MenuItem 
                            key={option.id} 
                            value={option.id}
                            onClick={() => {
                                if (option.id === 'Custom') {
                                    setIsDateModalOpen(true);
                                }
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                
                {dateFilter === 'Custom' && (
                    <IconButton 
                        onClick={() => setIsDateModalOpen(true)}
                        size="small"
                        sx={{ color: '#000000', height: '40px' }}
                        title="Edit Custom Dates"
                    >
                        <EditIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            <Header />
             <CssBaseline />
                <Box sx={{ width: '250px', position: 'fixed', height: '100vh' }}>
            <NavBar selected="Reports" /> 
            </Box>
            <QueryWidget />
            <div style={{ marginTop: "70px" }}>
                <Heading 
                    title="Reports" 
                    closeIcon="false" 
                    leftContent={
                        <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={2} 
                            sx={{ 
                                overflowX: 'auto', 
                                overflowY: 'hidden', 
                                flexWrap: 'nowrap',
                                scrollbarWidth: 'none', 
                                msOverflowStyle: 'none', 
                                '&::-webkit-scrollbar': {
                                    display: 'none' 
                                }
                            }}
                        >
                            {!isFilterOpen && renderReportAndDateFilters(false)}
                        </Box>
                    } 
                    rightContent={
                        <Box display="flex" alignItems="center" gap={1} sx={{ m: 0, p: 0, mt: '-8px' }}>
                            <Button 
                                onClick={(e) => isFilterOpen ? setFilterAnchorEl(null) : setFilterAnchorEl(e.currentTarget)} 
                                sx={{ 
                                    backgroundColor: isFilterOpen ? 'rgba(0,0,0,0.05)' : 'rgba(0, 0, 0, 0)', 
                                    color: 'rgba(0, 0, 0, 0.54)', 
                                    minWidth: '40px', 
                                    height: '40px',
                                    margin: 0,
                                    padding: 0
                                }}
                                aria-label="Filter"
                            >
                                <FilterAltIcon />
                            </Button>

                            <Button 
                                className="custom-button" 
                                onClick={handleOpenDownloadMenu} 
                                aria-controls={isDownloadMenuOpen ? 'download-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isDownloadMenuOpen ? 'true' : undefined}
                                sx={{ height: '40px', margin: 0, padding: 0 }}
                            >
                                <DownloadIcon />
                            </Button>
                            
                            <Menu
                                id="download-menu"
                                anchorEl={downloadMenuAnchor}
                                open={isDownloadMenuOpen}
                                onClose={handleCloseDownloadMenu}
                            >
                                <MenuItem onClick={() => handleDownloadFormat('Excel')}>Excel</MenuItem>
                                <MenuItem onClick={() => handleDownloadFormat('CSV')}>CSV</MenuItem>
                                <MenuItem onClick={() => handleDownloadFormat('PDF')}>PDF</MenuItem>
                            </Menu>
                        </Box>
                    } 
                />

                     <Box sx={{marginLeft: '180px' }}>
          <span>Show Entries:</span>
          <Dropdown
            options={showEntries}
            selectedValue={pageSize.toString()}
            handleChange={(event: any) => updateField(event)}
            width="80px"
            height="30px"
          />
        </Box>
                <Box 
                    className="table-container custom-data-grid" 
                >                  
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        hideFooterPagination
                        disableColumnResize
                        disableRowSelectionOnClick
                        autoHeight={false}
                        classes={{ columnHeader: 'red-header' }}
                    />
                </Box>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginLeft: '180px', marginBottom: '40px' }}>
                    <Newpagination 
                        rows={totalRecords} 
                        pageSize={pageSize} 
                        page={page} 
                        handlePageChange={handlePageClick} 
                    />
                </div>
            </div>

           <Popover
                open={isFilterOpen}
                onClose={() => setFilterAnchorEl(null)}
                anchorReference="anchorPosition"
                anchorPosition={{ top: 70, left: window.innerWidth / 2 }} 
                transformOrigin={{
                    vertical: 'top',  
                    horizontal: 'center', 
                }}
                sx={{ mt: 1 }} 
            >
                <Box sx={{ position: 'relative', pt: 5, pb: 2, px: { xs: 1.5, md: 2 } }}>
                    
                    <Box sx={{ position: 'absolute', top: 8, left: 16, zIndex: 1 }}>
                        <Button 
                            size="small" 
                            onClick={handleClearFilters} 
                            sx={{ 
                                textTransform: 'none', 
                                minWidth: 'auto', 
                                padding: '4px 16px',
                                fontWeight: 'bold',
                                color: '#000000',
                                backgroundColor: '#ececec',
                                border: '1px solid #d9d9d9',
                                borderRadius: '6px',
                                transition: 'all 0.1s ease',
                                '&:hover': {
                                    backgroundColor: '#a9a9a9',
                                },
                                '&:active': {
                                    transform: 'translateY(4px)',
                                    boxShadow: '0px 0px 0px #a0a0a0, 0px 1px 2px rgba(0,0,0,0.2)',
                                },
                               
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Box>

                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton
                            onClick={() => setFilterAnchorEl(null)}
                            size="small"
                            aria-label="close filter"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' }, 
                        alignItems: { xs: 'stretch', md: 'center' }, 
                        gap: 2, 
                        width: { xs: '85vw', md: 'max-content' },
                        maxWidth: { xs: '280px', sm: '400px', md: '100%' },
                         marginTop:'10px'
                    }}>
                        
                        {isFilterOpen && renderReportAndDateFilters(isMobile)}

                       {!isClientAdmin && hasClientFeature && (
                            <Box sx={{ width: { xs: '100%', md: '150px' } }}>
                                <Dropdown
                                    options={clientOptions}
                                    selectedValue={selectedClient.id}
                                    handleChange={(e: SelectChangeEvent<string>) => {
                                        const selectedId = e.target.value;
                                        const fullObj = clientOptions.find(c => c.id === selectedId) || { id: 'All', label: 'All Clients' };
                                        setSelectedClient(fullObj);
                                        setVehicleSearch(tempVehicleSearch);
                                        setPage('0'); 
                                    }}
                                    width="150px"
                                    height="40px"
                                />
                            </Box>
                        )}

                        {hasWarehouseFeature && (
                            <Box sx={{ width: { xs: '100%', md: '180px' } }}>
                                <Dropdown
                                    options={warehouseOptions}
                                    selectedValue={selectedWarehouse.id}
                                    handleChange={(e: SelectChangeEvent<string>) => {
                                        const selectedId = e.target.value;
                                        const fullObj = warehouseOptions.find(w => w.id === selectedId) || { id: 'All', label: 'All Warehouses' };
                                        setSelectedWarehouse(fullObj);
                                        setVehicleSearch(tempVehicleSearch);
                                        setPage('0');
                                    }}
                                    width="180px"
                                    height="40px"
                                />
                            </Box>
                        )}
                        
                        <TextField 
                            variant="outlined" 
                            placeholder="Enter Vehicle No..." 
                            value={tempVehicleSearch}
                            onChange={(e: { target: { value: any; }; }) => {
                                const newValue = e.target.value;
                                setTempVehicleSearch(newValue);
                                if (newValue.trim() === '') {
                                    setVehicleSearch('');
                                    setPage('0');
                                }
                            }}
                            onKeyDown={(e: { key: string; }) => {
                                if (e.key === 'Enter') handleApplyFilter();
                            }}
                            sx={{ 
                                width: { xs: '100%', md: '200px' }, 
                                '& .MuiInputBase-root': { height: '40px', paddingRight: '8px' } 
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={handleApplyFilter} 
                                            edge="end" 
                                            title="Search"
                                            sx={{ color: '#1976d2' }} 
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>
            </Popover>

            <Dialog 
                open={isDateModalOpen} 
                onClose={handleCloseModal}
                fullWidth
                maxWidth="sm"
            >
                <Box
                    sx={{
                        marginLeft: {
                        xs: '0px',
                        sm: '-180px',
                        },
                    }}
                > 
                    <Heading title="Select Custom Date Range" closeIcon='true' onClose={handleCloseModal} />
                </Box>
                
                <DialogContent 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' }, 
                        gap: 2, 
                        mt: 1 
                    }}
                >
                    <Box sx={{ width: '100%' }}>
                        <DatePicker 
                            id="fromDate" 
                            name="fromDate" 
                            value={customFromDate} 
                            label="From"
                            maxDate={customToDate ? new Date(customToDate) : undefined} 
                            onChange={(e: any) => {
                                const newFromDate = e.target.value;
                                setCustomFromDate(newFromDate);
                                if (customToDate && (newFromDate > customToDate)) {
                                    setCustomToDate('');
                                }
                            }} 
                            sx={{ width: '100%' }} 
                        />
                    </Box>
                    <Box sx={{ width: '100%' }}>
                        <DatePicker 
                            id="toDate" 
                            name="toDate" 
                            value={customToDate} 
                            label="To" 
                            onChange={(e: any) => setCustomToDate(e.target.value)} 
                            minDate={customFromDate ? new Date(customFromDate) : undefined} 
                            maxDate={getMaxToDate() ? new Date(getMaxToDate()!) : undefined} 
                            disabled={!customFromDate} 
                            sx={{ width: '100%' }}
                        />
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
                    <Button className="custom-cancel-button" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button className="custom-button" onClick={handleApplyCustomDates}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Reports;